import { useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { ToggleRight } from 'lucide-react';
import { toast } from 'sonner';
import type { CompanyUser } from '@/lib/types/mise/company';
import { countHubAccessToEnable } from '@/lib/mise/hubAccess';
import { useEnableAllHubAccess } from '@/lib/query/hooks/useUsers';
import { useCompanyAccounts } from '@/lib/query/hooks/useCompanies';

interface HubAccessBulkEnableButtonProps {
  users: CompanyUser[];
  companyId: string;
}

/**
 * 企業の全ユーザーの StoreHub アクセスを、各ユーザーがアクセスできる全アカウントで ON にするボタン。
 * 押すと確認ダイアログを出し、OK で一括 ON にする。
 */
export function HubAccessBulkEnableButton({ users, companyId }: HubAccessBulkEnableButtonProps) {
  const [open, setOpen] = useState(false);
  const enableAll = useEnableAllHubAccess(companyId);
  const { data: companyAccounts, isLoading: accountsLoading } = useCompanyAccounts(companyId);
  const { userCount, switchCount } = countHubAccessToEnable(
    users,
    (companyAccounts ?? []).map((a) => a.id),
  );

  const handleOpen = () => {
    enableAll.reset();
    setOpen(true);
  };

  const handleClose = () => {
    if (enableAll.isPending) return;
    setOpen(false);
  };

  const handleConfirm = async () => {
    try {
      await enableAll.mutateAsync();
      toast.success('StoreHub アクセスを全員 ON にしました');
      setOpen(false);
    } catch (error) {
      console.error('StoreHub アクセスの一括 ON に失敗しました:', error);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<ToggleRight size={16} />}
        onClick={handleOpen}
        // 全員 ON なら押しても何も変わらない。企業のアカウントが分かるまでは件数を出せない
        disabled={accountsLoading || !companyAccounts || switchCount === 0}
      >
        StoreHub を全員 ON
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>StoreHub アクセスを全員 ON にする</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {userCount}人のユーザーの、OFF になっている {switchCount} 件のアカウントを ON にします。
          </Typography>
          <Alert severity="info" sx={{ mb: enableAll.isError ? 2 : 0 }}>
            各ユーザーがアクセスできるアカウントのスイッチがすべて ON になります。意図して OFF
            にしているユーザーも ON になるため、必要なら後から個別に OFF にしてください。
          </Alert>
          {enableAll.isError && (
            <Alert severity="error">
              {enableAll.error.message || 'StoreHub アクセスの一括 ON に失敗しました'}
              （途中まで ON になっている場合があります。もう一度実行すると残りも ON になります）
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={enableAll.isPending}>
            キャンセル
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={enableAll.isPending}
            startIcon={enableAll.isPending ? <CircularProgress size={16} /> : undefined}
          >
            全員 ON にする
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
